// PM-Einstellungen — EIN Admin-Ort fuer die verstreute PM-Konfiguration (TODO.md):
//   1. Auftragsarten-Mapping (general_settings 'pm_order_types'): SAP-Code -> Anzeigename DE/EN
//      + Wizard-Default. DER Hebel beim Kunden-Onboarding — kein Code-Deploy noetig.
//   2. Feature-Flags (pm_features): experimentelle Funktionen, fail-closed.
//   3. Rollen-Uebersicht (informativ): welche PM-Rolle darf was; Zuordnung passiert im
//      Berechtigungsprofil (Tab "Rollen" -> PM-Rolle-Dropdown), nicht hier.
// Serverseitige Gates: alle Schreib-Endpunkte verlangen das PM-Recht canManageUsers.
import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Save, RefreshCw, X, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { me as fetchPmMe, getOrderTypesAdmin, saveOrderTypesAdmin, savePmFeatures, getScopeAdmin, saveScopeAdmin, getSapDiscovery } from '../../modules/plant-maintenance/api';

/** Chip list with remove + free-text add — the editing baseline WITHOUT discovery (mock
 *  adapter / SAP unreachable). With discovery, picklists appear below it. */
function ChipList({ label, values, onRemove, onAdd, isDarkMode, addPlaceholder }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim().toUpperCase();
    if (v && !values.includes(v)) onAdd(v);
    setDraft('');
  };
  return (
    <div>
      <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{label}</div>
      <div className="flex flex-wrap items-center gap-1.5">
        {values.map((v) => (
          <span key={v} className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm ${isDarkMode ? 'bg-white/[0.08] text-white/85' : 'bg-slate-100 text-slate-700'}`}>
            {v}
            <button onClick={() => onRemove(v)} aria-label={`remove ${v}`} className={isDarkMode ? 'text-white/40 hover:text-red-300' : 'text-slate-400 hover:text-red-600'}>
              <X className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          onBlur={add}
          placeholder={addPlaceholder}
          className={`px-2 py-1 rounded-lg text-sm border outline-none w-28 ${isDarkMode
            ? 'bg-gray-900/50 border-white/10 text-white placeholder-white/30'
            : 'bg-white border-slate-300 text-slate-800 placeholder-slate-400'}`}
        />
      </div>
    </div>
  );
}

const PM_ROLES_OVERVIEW = [
  { id: 'mechanic',   rights: ['create', 'notify', 'confirm', 'components'] },
  { id: 'supervisor', rights: ['create', 'notify', 'release', 'teco', 'admin'] },
  { id: 'controller', rights: ['close'] },
];

function PmSettingsPanel({ user, showSuccess, showError }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [orderTypes, setOrderTypes] = useState([]);
  const [features, setFeatures] = useState({ voice: false, strictRoles: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Instance scope (general_settings 'pm_scope') + SAP discovery as the picklist source.
  // discovery === null: not loaded yet (or mock adapter -> 501, stays null; the scope remains
  // editable via chips + free text, just without picklists).
  const [scope, setScope] = useState(null);
  const [discovery, setDiscovery] = useState(null);
  const [discovering, setDiscovering] = useState(false);
  const [wcFilter, setWcFilter] = useState('');
  const [savingScope, setSavingScope] = useState(false);
  // Which order type's readiness detail is expanded ((i) next to the ready/incomplete badge).
  // Inline expand, not a popover — same touch-first reasoning as pm/ui.js Row.
  const [otInfoOpen, setOtInfoOpen] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      getOrderTypesAdmin(user).catch(() => null),
      fetchPmMe(user).catch(() => null),
      getScopeAdmin(user).catch(() => null),
    ]).then(([ot, meRes, sc]) => {
      if (!alive) return;
      if (ot?.orderTypes) setOrderTypes(ot.orderTypes);
      if (meRes?.features) setFeatures({ voice: Boolean(meRes.features.voice), strictRoles: Boolean(meRes.features.strictRoles) });
      if (sc?.scope) setScope(sc.scope);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [user]);

  // Load discovery for the detail plant: picklists for work centers/order types. The detail
  // plant is the FIRST plant in the scope — to configure a different plant, put it there first
  // (add/remove chips), then reload. Deliberately no auto-load: the call goes out to the SAP
  // system, and the admin should see WHEN that happens.
  const runDiscovery = async () => {
    setDiscovering(true);
    try {
      const plant = scope?.plants?.[0];
      const d = await getSapDiscovery(user, plant);
      if (d?.ok === false || !d?.plants) throw new Error(d?.error || t('admin.pm.scopeDiscoverFail'));
      setDiscovery(d);
      showSuccess?.(t('admin.pm.scopeDiscovered'));
    } catch (e) {
      showError?.(e?.message || t('admin.pm.scopeDiscoverFail'));
    } finally {
      setDiscovering(false);
    }
  };

  const toggleIn = (listKey, value) => {
    setScope((s) => {
      const has = s[listKey].includes(value);
      const next = has ? s[listKey].filter((v) => v !== value) : [...s[listKey], value];
      return { ...s, [listKey]: next };
    });
  };

  const persistScope = async () => {
    if (!scope?.plants?.length || !scope?.workCenters?.length || !scope?.orderTypes?.length) {
      showError?.(t('admin.pm.scopeEmpty')); return;
    }
    setSavingScope(true);
    try {
      // Take plant display names from discovery where present (keep existing otherwise).
      const names = { ...(scope.plantNames || {}) };
      for (const p of discovery?.plants || []) {
        if (scope.plants.includes(p.plant) && p.name) names[p.plant] = p.name;
      }
      const res = await saveScopeAdmin(user, { ...scope, plantNames: names });
      if (res?.ok === false) throw new Error(res.error);
      setScope(res.scope);
      showSuccess?.(t('admin.pm.scopeSaved'));
    } catch (e) {
      showError?.(e?.message || 'error');
    } finally {
      setSavingScope(false);
    }
  };

  const setOt = (idx, field, value) => {
    setOrderTypes((list) => list.map((o, i) => (i === idx ? { ...o, [field]: value } : o)));
  };
  const setDefault = (idx) => {
    setOrderTypes((list) => list.map((o, i) => ({ ...o, isDefault: i === idx })));
  };
  const addRow = () => setOrderTypes((list) => [...list, { code: '', labelDe: '', labelEn: '', isDefault: list.length === 0 }]);
  const removeRow = (idx) => setOrderTypes((list) => {
    const next = list.filter((_, i) => i !== idx);
    if (next.length && !next.some((o) => o.isDefault)) next[0] = { ...next[0], isDefault: true };
    return next;
  });

  const saveTypes = async () => {
    // Client-Vorpruefung spiegelt die Server-Validierung (2-4 Zeichen A-Z0-9) — der Server bleibt autoritativ.
    const bad = orderTypes.find((o) => !/^[A-Z0-9]{2,4}$/.test(o.code || ''));
    if (bad !== undefined) { showError?.(t('admin.pm.badCode')); return; }
    setSaving(true);
    try {
      const res = await saveOrderTypesAdmin(user, orderTypes);
      if (res?.ok === false) showError?.(res.error || 'error');
      else { setOrderTypes(res.orderTypes); showSuccess?.(t('admin.pm.savedTypes')); }
    } catch (e) {
      showError?.(e?.message || 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = async (key, checked) => {
    setFeatures((f) => ({ ...f, [key]: checked }));
    try {
      const res = await savePmFeatures(user, { [key]: checked });
      if (res?.ok === false) throw new Error(res.error);
      showSuccess?.(t('admin.pm.savedFeatures'));
    } catch (e) {
      setFeatures((f) => ({ ...f, [key]: !checked }));
      showError?.(e?.message || 'error');
    }
  };

  const inputCls = `px-2 py-1.5 rounded-lg text-sm border outline-none w-full ${isDarkMode
    ? 'bg-gray-900/50 border-white/10 text-white'
    : 'bg-white border-slate-300 text-slate-800'}`;
  const headCls = `text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`;

  if (loading) {
    return <p className={`py-8 text-center ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>{t('pm.common.loading')}</p>;
  }

  const wcSelectable = (discovery?.plantDetail?.workCenters || [])
    .filter((w) => !wcFilter || w.id.toLowerCase().includes(wcFilter.toLowerCase())
      || (w.description || '').toLowerCase().includes(wcFilter.toLowerCase()));

  return (
    <div className="space-y-8">
      {/* 0. Instance scope: plants / work centers / order types of THIS SAP system */}
      {scope && (
        <section>
          <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {t('admin.pm.scopeTitle')}
          </h3>
          <p className={`text-sm mb-3 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
            {t('admin.pm.scopeDesc')}
          </p>
          <div className="space-y-3">
            <ChipList label={t('admin.pm.scopePlants')} values={scope.plants} isDarkMode={isDarkMode}
              onRemove={(v) => toggleIn('plants', v)} onAdd={(v) => toggleIn('plants', v)}
              addPlaceholder={t('admin.pm.scopeAdd')} />
            <ChipList label={t('admin.pm.scopeWorkCenters')} values={scope.workCenters} isDarkMode={isDarkMode}
              onRemove={(v) => toggleIn('workCenters', v)} onAdd={(v) => toggleIn('workCenters', v)}
              addPlaceholder={t('admin.pm.scopeAdd')} />
            <ChipList label={t('admin.pm.scopeOrderTypes')} values={scope.orderTypes} isDarkMode={isDarkMode}
              onRemove={(v) => toggleIn('orderTypes', v)} onAdd={(v) => toggleIn('orderTypes', v)}
              addPlaceholder={t('admin.pm.scopeAdd')} />
          </div>

          <div className="flex gap-2 mt-4">
            <button onClick={runDiscovery} disabled={discovering}
              className={`px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-1 ${isDarkMode ? 'bg-white/[0.08] text-white/80' : 'bg-slate-100 text-slate-700'} disabled:opacity-40`}>
              <RefreshCw className={`w-4 h-4 ${discovering ? 'animate-spin' : ''}`} />
              {discovering ? t('admin.pm.scopeDiscovering') : t('admin.pm.scopeDiscover')}
            </button>
            <button onClick={persistScope} disabled={savingScope}
              className="px-3 py-2 rounded-xl text-sm font-semibold flex items-center gap-1 bg-emerald-600 text-white disabled:opacity-40">
              <Save className="w-4 h-4" /> {savingScope ? t('admin.pm.saving') : t('admin.pm.scopeSave')}
            </button>
          </div>

          {discovery && (
            <div className={`mt-4 p-3 rounded-xl border space-y-4 ${isDarkMode ? 'bg-gray-800/30 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
              <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                {t('admin.pm.scopeDiscoveryNote')
                  .replace('{sysid}', discovery.system?.sysid || '?')
                  .replace('{client}', discovery.system?.client || '?')}
              </p>

              {/* Plants: all maintenance planning plants of the system */}
              <div>
                <div className={headCls}>{t('admin.pm.scopePlants')}</div>
                <div className="mt-1 max-h-40 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                  {(discovery.plants || []).map((p) => (
                    <label key={p.plant} className={`flex items-center gap-2 py-0.5 text-sm cursor-pointer ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>
                      <input type="checkbox" className="rounded" checked={scope.plants.includes(p.plant)} onChange={() => toggleIn('plants', p.plant)} />
                      <span className="font-mono">{p.plant}</span>
                      <span className={isDarkMode ? 'text-white/40' : 'text-slate-400'}>{p.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {discovery.plantDetail && (
                <>
                  {/* Work centers of the detail plant (first scope plant at load time) */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div className={headCls}>
                        {t('admin.pm.scopeWorkCentersOf').replace('{plant}', discovery.plantDetail.plant)}
                      </div>
                      <input value={wcFilter} onChange={(e) => setWcFilter(e.target.value)}
                        placeholder={t('admin.pm.scopeFilter')}
                        className={`px-2 py-1 rounded-lg text-xs border outline-none w-36 ${isDarkMode
                          ? 'bg-gray-900/50 border-white/10 text-white placeholder-white/30'
                          : 'bg-white border-slate-300 text-slate-800 placeholder-slate-400'}`} />
                    </div>
                    <div className="mt-1 max-h-48 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                      {wcSelectable.map((w) => (
                        <label key={w.id} className={`flex items-center gap-2 py-0.5 text-sm cursor-pointer ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>
                          <input type="checkbox" className="rounded" checked={scope.workCenters.includes(w.id)} onChange={() => toggleIn('workCenters', w.id)} />
                          <span className="font-mono">{w.id}</span>
                          <span className={`truncate ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{w.description}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Order types with readiness from the six-table check */}
                  <div>
                    <div className={headCls}>
                      {t('admin.pm.scopeOrderTypesOf').replace('{plant}', discovery.plantDetail.plant)}
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {(discovery.plantDetail.orderTypes || []).map((ot) => {
                        // The four checks behind ready/incomplete — one entry per customizing
                        // table, each with the fix location, so an admin can act on "incomplete"
                        // without asking a consultant. The activity-type check also shows WHICH
                        // ILARTs the type accepts (the create op falls back to the first one).
                        const checks = [
                          { key: 'scheduling',   ok: ot.scheduling },
                          { key: 'costing',      ok: ot.costing },
                          { key: 'confirmation', ok: ot.confirmation },
                          { key: 'activityTypes', ok: (ot.activityTypes || []).length > 0,
                            extra: (ot.activityTypes || []).join(', ') },
                        ];
                        const open = otInfoOpen === ot.type;
                        return (
                          <div key={ot.type}>
                            <label className={`flex items-center gap-2 py-0.5 text-sm cursor-pointer ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>
                              <input type="checkbox" className="rounded" checked={scope.orderTypes.includes(ot.type)} onChange={() => toggleIn('orderTypes', ot.type)} />
                              <span className="font-mono">{ot.type}</span>
                              <span className={`truncate ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{ot.text}</span>
                              {ot.ready
                                ? <span className="inline-flex items-center gap-1 text-xs text-emerald-500"><CheckCircle2 className="w-3.5 h-3.5" /> {t('admin.pm.scopeReady')}</span>
                                : <span className="inline-flex items-center gap-1 text-xs text-amber-500"><AlertTriangle className="w-3.5 h-3.5" /> {t('admin.pm.scopeNotReady')}</span>}
                              <button type="button"
                                onClick={(e) => { e.preventDefault(); setOtInfoOpen(open ? null : ot.type); }}
                                aria-label={`Info ${ot.type}`} aria-expanded={open}
                                className={`p-0.5 rounded-full transition-colors ${open
                                  ? (isDarkMode ? 'text-amber-300' : 'text-amber-600')
                                  : (isDarkMode ? 'text-white/35 hover:text-white/70' : 'text-slate-300 hover:text-slate-500')}`}>
                                <Info className="w-3.5 h-3.5" />
                              </button>
                            </label>
                            {open && (
                              <div className={`ml-6 mb-1 mt-0.5 rounded-lg px-2.5 py-1.5 text-xs space-y-0.5 ${isDarkMode ? 'bg-white/[0.06] text-white/70' : 'bg-slate-100 text-slate-600'}`}>
                                {checks.map((c) => (
                                  <div key={c.key} className="flex items-start gap-1.5">
                                    {c.ok
                                      ? <CheckCircle2 className="w-3.5 h-3.5 mt-px shrink-0 text-emerald-500" />
                                      : <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0 text-amber-500" />}
                                    <span>
                                      {t(`admin.pm.scopeChk.${c.key}`)}
                                      {c.key === 'activityTypes' && c.ok && <span className="font-mono"> {c.extra}</span>}
                                      {!c.ok && <span> — {t(`admin.pm.scopeChkFix.${c.key}`)}</span>}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </section>
      )}

      {/* 1. Auftragsarten-Mapping */}
      <section>
        <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {t('admin.pm.orderTypesTitle')}
        </h3>
        <p className={`text-sm mb-3 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
          {t('admin.pm.orderTypesDesc')}
        </p>
        <div className="space-y-2">
          <div className="grid grid-cols-[70px_1fr_1fr_70px_36px] gap-2 items-center px-1">
            <span className={headCls}>{t('admin.pm.colCode')}</span>
            <span className={headCls}>{t('admin.pm.colLabelDe')}</span>
            <span className={headCls}>{t('admin.pm.colLabelEn')}</span>
            <span className={headCls}>{t('admin.pm.colDefault')}</span>
            <span />
          </div>
          {orderTypes.map((o, idx) => (
            <div key={idx} className="grid grid-cols-[70px_1fr_1fr_70px_36px] gap-2 items-center">
              <input value={o.code} onChange={(e) => setOt(idx, 'code', e.target.value.toUpperCase())} maxLength={4} className={inputCls} />
              <input value={o.labelDe} onChange={(e) => setOt(idx, 'labelDe', e.target.value)} className={inputCls} />
              <input value={o.labelEn} onChange={(e) => setOt(idx, 'labelEn', e.target.value)} className={inputCls} />
              <div className="flex justify-center">
                <input type="radio" name="pm-ot-default" checked={o.isDefault === true} onChange={() => setDefault(idx)} />
              </div>
              <button onClick={() => removeRow(idx)} aria-label="remove" className={`p-1.5 rounded-lg ${isDarkMode ? 'text-white/40 hover:text-red-300' : 'text-slate-400 hover:text-red-600'}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={addRow} className={`px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-1 ${isDarkMode ? 'bg-white/[0.08] text-white/80' : 'bg-slate-100 text-slate-700'}`}>
            <Plus className="w-4 h-4" /> {t('admin.pm.addType')}
          </button>
          <button onClick={saveTypes} disabled={saving || orderTypes.length === 0}
            className="px-3 py-2 rounded-xl text-sm font-semibold flex items-center gap-1 bg-emerald-600 text-white disabled:opacity-40">
            <Save className="w-4 h-4" /> {saving ? t('admin.pm.saving') : t('admin.pm.saveTypes')}
          </button>
        </div>
        <p className={`text-xs mt-2 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
          {t('admin.pm.orderTypesNote')}
        </p>
      </section>

      {/* 2. Feature-Flags */}
      <section>
        <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {t('admin.pm.featuresTitle')}
        </h3>
        <label className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer border ${isDarkMode ? 'bg-gray-800/30 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
          <input type="checkbox" checked={features.voice} onChange={(e) => toggleFeature('voice', e.target.checked)} className="mt-1 rounded" />
          <div>
            <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('admin.pm.voiceLabel')}</div>
            <div className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>{t('admin.pm.voiceDesc')}</div>
          </div>
        </label>
        <label className={`flex items-start gap-3 p-3 mt-2 rounded-xl cursor-pointer border ${isDarkMode ? 'bg-gray-800/30 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
          <input type="checkbox" checked={features.strictRoles} onChange={(e) => toggleFeature('strictRoles', e.target.checked)} className="mt-1 rounded" />
          <div>
            <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('admin.pm.strictLabel')}</div>
            <div className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>{t('admin.pm.strictDesc')}</div>
          </div>
        </label>
      </section>

      {/* 3. Rollen-Uebersicht (informativ) */}
      <section>
        <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {t('admin.pm.rolesTitle')}
        </h3>
        <p className={`text-sm mb-3 ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
          {t('admin.pm.rolesDesc')}
        </p>
        <div className="space-y-2">
          {PM_ROLES_OVERVIEW.map((r) => (
            <div key={r.id} className={`p-3 rounded-xl border ${isDarkMode ? 'bg-gray-800/30 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
              <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t(`pm.role.${r.id}`)}</div>
              <div className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-gray-500'}`}>
                {r.rights.map((k) => t(`admin.pm.right.${k}`)).join(' · ')}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default PmSettingsPanel;
